import axios from 'axios';
import { getToken } from '../utils/getToken';
import { store } from '../redux/store';
import { toast } from '../components/AppToast';
import { getApiErrorMessage } from '../utils/apiErrorMessage';


// Add global handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled Promise Rejection:', event.reason);
  
  // Don't show toast for 401 errors as they're handled by the interceptor
  if (event.reason?.response?.status !== 401) {
    toast.error('An unexpected error occurred. Please try again later.');
  }
  
  // Prevent the default browser behavior (console error)
  event.preventDefault();
});
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api/',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Dashboard "All warehouses" opts out. An explicit location_id (warehouse filter) is kept.
    const state = store.getState();
    const selectedFacility = state?.facility?.selectedFacility;
    const explicitLocation = config.headers?.location_id || config.headers?.get?.("location_id");
    if (config.skipLocationHeader) {
      if (typeof config.headers?.delete === "function") config.headers.delete("location_id");
      else delete config.headers.location_id;
    } else if (explicitLocation) {
      config.headers.location_id = String(explicitLocation).toLowerCase();
    } else if (selectedFacility?.value) {
      config.headers.location_id = selectedFacility.value.toLowerCase();
    }
    
    const isAuthSession = /(^|\/)auth\/(login|logout)\/?$/.test(config.url || '');
    store.dispatch({ type: 'SHOW_LOADER', payload: { variant: isAuthSession ? 'brand' : 'dots' } });
    return config;
  },
  (error) => {
    store.dispatch({ type: 'HIDE_LOADER' });
    return Promise.reject(error);
  }
);

// Response Interceptor
api.interceptors.response.use(
  (response) => {
    store.dispatch({ type: 'HIDE_LOADER' });
    return response;
  },
  async (error) => {
    store.dispatch({ type: 'HIDE_LOADER' });
    
    const originalRequest = error.config;
    
    // Prevent infinite refresh loops
    if (originalRequest && originalRequest._isRetry) {
      // Already tried refreshing once, don't try again
      store.dispatch({ type: "RESET_STORE" });
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Check if error is due to unauthorized access (401)
    if (error.response && error.response.status === 401 && !originalRequest._isRetry) {
      originalRequest._isRetry = true;
      
      try {
        // Dispatch logout action if token is invalid
        store.dispatch({ type: "RESET_STORE" });
        
        // Redirect to login page
        window.location.href = '/login';
        
        toast.error('Your session has expired. Please log in again.');
        
        // Don't retry the original request, just reject it
        return Promise.reject(new Error('Session expired'));
      } catch (refreshError) {
        // If token refresh fails, redirect to login
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // For other errors, show a toast message if it's a response error
    if (error.response) {
      const errorMessage = getApiErrorMessage(error);
                          
      // Don't show toast for 401 as we already handle it above
      if (error.response.status !== 401) {
        toast.error(errorMessage);
      }
    } else if (error.request) {
      // Request was made but no response was received
      toast.error('No response from server. Please check your connection.');
    } else {
      // Something else caused the error
      toast.error('An error occurred while processing your request.');
    }

    return Promise.reject(error);
  }
);

export default api;
